import type {
  Product,
  ProductCategoriesInner,
  ProductCategoryDetail,
  ProductDimensions,
  ProductImage,
  ProductOffer,
  ProductVariant,
} from "@sakura-tech-lab/dashboard-api";
import type { AttributeSchema, CategoryTreeNode } from "@/services/category.service";

export interface NormalizedCategory {
  readonly id: string;
  readonly name: string;
  readonly parentName: string | null;
}

export interface ResolvedCategoryPath {
  readonly id: string;
  readonly names: readonly string[];
}

export interface DetailEntry {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function availableLanguages(product: Product): string[] {
  const languages = new Set<string>();
  const addLocalizedMap = (field: Record<string, unknown> | null | undefined) => {
    for (const [language, value] of Object.entries(field ?? {})) {
      if (formatDataValue(value)) languages.add(language);
    }
  };

  addLocalizedMap(product.name);
  addLocalizedMap(product.description);

  for (const variant of product.variants ?? []) {
    addLocalizedMap(variant.name);
    addLocalizedMap(variant.description);
    addLocalizedMap(variant.originalName);
    addLocalizedMap(variant.originalDescription);

    for (const translations of Object.values(variant.attributesTranslated ?? {})) {
      addLocalizedMap(translations);
    }
    for (const offer of variant.offers) addLocalizedMap(offer.conditionNotes);
  }
  for (const offer of product.offers ?? []) addLocalizedMap(offer.conditionNotes);

  return [...languages].sort((left, right) => {
    if (left === "en") return -1;
    if (right === "en") return 1;
    return left.localeCompare(right);
  });
}

export function pickLocalized(
  field: Record<string, unknown> | null | undefined,
  language: string,
): string {
  if (!field) return "";
  return (
    formatDataValue(field[language]) ||
    formatDataValue(field.en) ||
    Object.values(field).map(formatDataValue).find(Boolean) ||
    ""
  );
}

export function getProductTitle(
  product: Product,
  variant: ProductVariant | null | undefined,
  language: string,
): string {
  return (
    pickLocalized(variant?.name, language) ||
    pickLocalized(variant?.originalName, language) ||
    pickLocalized(product.name, language) ||
    product.productLabel ||
    product.caption ||
    "Product detail"
  );
}

export function getProductDescription(
  product: Product,
  variant: ProductVariant | null | undefined,
  language: string,
): string {
  return (
    pickLocalized(variant?.description, language) ||
    pickLocalized(variant?.originalDescription, language) ||
    pickLocalized(product.description, language)
  );
}

export function getVariantImages(
  product: Product,
  variant: ProductVariant | null | undefined,
): ProductImage[] {
  if (!variant) return uniqueImages(product.images);

  if (variant.images.length > 0) return uniqueImages(variant.images);

  const scopedImages = product.images.filter((image) => image.variantId === variant.id);
  if (scopedImages.length > 0) return uniqueImages(scopedImages);

  return uniqueImages(product.images.filter((image) => !image.variantId));
}

export function getProductOffers(product: Product): ProductOffer[] {
  const offers = new Map<string, ProductOffer>();
  for (const variant of product.variants ?? []) {
    for (const offer of variant.offers) offers.set(offer.id, offer);
  }
  for (const offer of product.offers ?? []) offers.set(offer.id, offer);
  return [...offers.values()];
}

export function getVariantOffers(
  product: Product,
  variant: ProductVariant | null | undefined,
): ProductOffer[] {
  if (!variant) return getProductOffers(product);

  const offers = new Map<string, ProductOffer>();
  for (const offer of variant.offers) offers.set(offer.id, offer);
  for (const offer of product.offers ?? []) {
    if (offer.variantId === variant.id) offers.set(offer.id, offer);
  }
  return [...offers.values()];
}

export function getRepresentativeOffer(
  product: Product,
  variant: ProductVariant | null | undefined,
): ProductOffer | null {
  const offers = getVariantOffers(product, variant);
  return (
    offers.find((offer) => offer.isPublished && offer.inStock) ??
    offers.find((offer) => offer.isPublished) ??
    offers.find((offer) => offer.inStock) ??
    offers[0] ??
    null
  );
}

export function formatPrice(price: Record<string, number> | null | undefined): string | null {
  const [currency, amount] = Object.entries(price ?? {}).find(([, value]) => Number.isFinite(value)) ?? [];
  if (!currency || amount === undefined) return null;

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      currencyDisplay: "code",
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
}

export function formatDateTime(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export type ProductCategoryAssignment = ProductCategoryDetail | ProductCategoriesInner;

export function normalizeCategory(
  category: ProductCategoryAssignment,
  language = "en",
): NormalizedCategory {
  if (typeof category === "string") {
    return { id: category, name: category, parentName: null };
  }
  const name = typeof category.name === "string"
    ? category.name
    : pickLocalized(category.name, language);
  return {
    id: category.id ?? name,
    name,
    parentName: "parentName" in category ? category.parentName ?? null : null,
  };
}

/** Resolve every assigned category against the catalogue tree so the UI can
 * show the complete root-to-leaf path. Older/string product payloads fall
 * back to their available name and immediate parent without hiding the leaf. */
export function resolveCategoryPaths(
  categories: readonly ProductCategoryAssignment[] | null | undefined,
  tree: readonly CategoryTreeNode[] | null | undefined,
  language = "en",
): ResolvedCategoryPath[] {
  const pathsById = new Map<string, readonly string[]>();
  const pathsByName = new Map<string, Array<readonly string[]>>();
  const normalizedName = (value: string) => value.trim().toLocaleLowerCase();

  const visit = (nodes: readonly CategoryTreeNode[], ancestors: readonly string[]) => {
    for (const node of nodes) {
      const name = pickLocalized(node.name, language);
      const names = [...ancestors, name];
      pathsById.set(node.id, names);
      const nameKey = normalizedName(name);
      pathsByName.set(nameKey, [...(pathsByName.get(nameKey) ?? []), names]);
      visit(node.children ?? [], names);
    }
  };
  visit(tree ?? [], []);

  return (categories ?? []).map((category) => {
    const normalized = normalizeCategory(category, language);
    const byId = pathsById.get(normalized.id);
    const byName = pathsByName.get(normalizedName(normalized.name));
    const resolved = byId ?? (byName?.length === 1 ? byName[0] : undefined);
    const fallback = [normalized.parentName, normalized.name]
      .filter((name): name is string => Boolean(name?.trim()))
      .filter((name, index, names) => index === 0 || name !== names[index - 1]);
    return {
      id: normalized.id,
      names: resolved ?? fallback,
    };
  });
}

export function humanizeKey(key: string): string {
  const normalized = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return normalized
    .split(" ")
    .map((word) => {
      const lower = word.toLowerCase();
      if (["ean", "gtin", "id", "kg", "sku", "upc", "url"].includes(lower)) {
        return lower.toUpperCase();
      }
      if (["cm", "mm"].includes(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

export function formatDataValue(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return Number.isFinite(value) ? formatNumber(value) : "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value instanceof Date) return formatDateTime(value) ?? "";

  if (Array.isArray(value)) {
    return value.map(formatDataValue).filter(Boolean).join(", ");
  }

  if (isRecord(value)) {
    return Object.entries(value)
      .flatMap(([key, item]) => {
        const formatted = formatDataValue(item);
        return formatted ? [`${humanizeKey(key)}: ${formatted}`] : [];
      })
      .join(" · ");
  }

  return "";
}

export function getSpecificationEntries(
  product: Product,
  variant: ProductVariant | null | undefined,
  language: string,
  attributeSchema?: AttributeSchema,
): DetailEntry[] {
  const attributes = variant ? variant.attributes : product.attributes;
  const translations = variant?.attributesTranslated ?? {};
  const keys = new Set([...Object.keys(attributes ?? {}), ...Object.keys(translations)]);

  return [...keys].flatMap((key) => {
    const translated = pickLocalized(translations[key], language);
    const value = translated || formatDataValue(attributes?.[key]);
    const label = pickLocalized(attributeSchema?.[key]?.label, language) || humanizeKey(key);
    return value ? [{ label, value }] : [];
  });
}

export function getVariantLabels(product: Product, language: string): Map<string, string> {
  const variants = product.variants ?? [];
  const variantValueKeys = uniqueKeys(variants.map((variant) => variant.variantValues));
  const dimensionKeys = uniqueKeys(variants.map((variant) => variant.dimensions));
  const varyingVariantKeys = variantValueKeys.filter((key) =>
    hasMeaningfulDifference(variants.map((variant) => formatDataValue(variant.variantValues?.[key]))),
  );
  const varyingDimensionKeys = dimensionKeys.filter((key) =>
    hasMeaningfulDifference(
      variants.map((variant) =>
        formatDataValue(variant.dimensions?.[key as keyof ProductDimensions]),
      ),
    ),
  );

  const labels = new Map<string, string>();
  const usedLabels = new Map<string, number>();

  variants.forEach((variant, index) => {
    const axisValues = (product.variantAxes ?? []).flatMap((axis) => {
      const value = findVariantValue(variant.variantValues, axis);
      return value ? [capitalize(value)] : [];
    });

    let label = axisValues.slice(0, 2).join(" / ");
    if (!label) {
      const nonColourKey = varyingVariantKeys.find(
        (key) => !["color", "colour"].includes(key.toLowerCase()),
      );
      const colourKey = varyingVariantKeys.find((key) =>
        ["color", "colour"].includes(key.toLowerCase()),
      );
      const variantKey = nonColourKey ?? colourKey;
      if (variantKey) label = capitalize(formatDataValue(variant.variantValues?.[variantKey]));
    }
    if (!label && varyingDimensionKeys.length > 0) {
      label = formatDataValue(
        variant.dimensions?.[varyingDimensionKeys[0] as keyof ProductDimensions],
      );
    }
    if (!label) {
      const name =
        pickLocalized(variant.name, language) || pickLocalized(variant.originalName, language);
      label = name.split(/\s+/).filter(Boolean).at(-1) ?? `Variant ${index + 1}`;
    }

    const duplicateCount = usedLabels.get(label) ?? 0;
    usedLabels.set(label, duplicateCount + 1);
    labels.set(variant.id, duplicateCount === 0 ? label : `${label} · ${duplicateCount + 1}`);
  });

  return labels;
}

function findVariantValue(
  values: Record<string, unknown> | null | undefined,
  axis: string,
): string {
  const direct = formatDataValue(values?.[axis]);
  if (direct) return direct;

  const normalizedAxis = normalizeKey(axis);
  const matchingKey = Object.keys(values ?? {}).find((key) => normalizeKey(key) === normalizedAxis);
  return matchingKey ? formatDataValue(values?.[matchingKey]) : "";
}

function uniqueImages(images: readonly ProductImage[]): ProductImage[] {
  const unique = new Map<string, ProductImage>();
  for (const image of images) unique.set(image.id || image.url, image);
  return [...unique.values()].sort(
    (left, right) => Number(Boolean(right.isPrimary)) - Number(Boolean(left.isPrimary)),
  );
}

function uniqueKeys(values: Array<Record<string, unknown> | object | null | undefined>): string[] {
  const keys = new Set<string>();
  for (const value of values) Object.keys(value ?? {}).forEach((key) => keys.add(key));
  return [...keys];
}

function hasMeaningfulDifference(values: readonly string[]): boolean {
  return values.some(Boolean) && new Set(values).size > 1;
}

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(value);
}
